import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteData } from '../data/SiteDataContext';
import { resolveMediaUrl } from '../../utils/media';
import { getSeriesPath } from '../lib/format';

// Parts listed per card before the "all parts" link takes over.
const PARTS_PREVIEW_LIMIT = 6;

function SeriesIndexCard({ entry, postById }) {
    const href = getSeriesPath(entry);
    const partCount = entry.parts.length;
    const coverPost = entry.parts
        .map((part) => postById.get(String(part.postId)))
        .find((post) => post && post.thumbnail);
    const coverSrc = coverPost ? resolveMediaUrl(coverPost.thumbnail) : '';
    const shownParts = entry.parts.slice(0, PARTS_PREVIEW_LIMIT);
    const hiddenCount = partCount - shownParts.length;

    return (
        <article className="series-index-card" role="listitem">
            {coverSrc && (
                <Link className="series-index-thumb grain-wrap" to={href} tabIndex={-1} aria-hidden="true">
                    <img src={coverSrc} alt="" loading="lazy" />
                </Link>
            )}
            <div className="series-index-body">
                <div className="eyebrow">{`${partCount} part${partCount === 1 ? '' : 's'}`}</div>
                <h2 className="series-index-title">
                    <Link to={href}>{entry.name}</Link>
                </h2>
                {entry.description && (
                    <p className="series-index-description">{entry.description}</p>
                )}
                <ol className="series-index-parts">
                    {shownParts.map((part) => (
                        <li key={part.postId}>
                            <Link to={`/posts/${part.postId}`}>
                                <span className="series-index-part-number">{`Part ${part.part}`}</span>
                                <span className="series-index-part-title">{part.title}</span>
                            </Link>
                        </li>
                    ))}
                </ol>
                {hiddenCount > 0 && (
                    <Link className="series-index-more" to={href}>
                        {`+ ${hiddenCount} more part${hiddenCount === 1 ? '' : 's'} →`}
                    </Link>
                )}
            </div>
        </article>
    );
}

// Every series on the site: writer-created ones and those detected from
// "… PART N" titles.
function SeriesIndexPage() {
    const { status, seriesStatus, posts, series } = useSiteData();

    if (status === 'loading') {
        return <p className="small-meta site-loading">Loading …</p>;
    }

    if (status === 'error') {
        return <p className="small-meta site-loading">Failed to load documents. Refresh to retry.</p>;
    }

    const postById = new Map(posts.map((post) => [String(post._id), post]));

    return (
        <>
            <header className="archive-header series-header">
                <div className="eyebrow" style={{ marginBottom: '.4rem' }}>Series</div>
                <h1>All Series</h1>
                <p className="series-header-description">
                    Multi-part writing, meant to be read in order.
                </p>
            </header>
            {series.length === 0 ? (
                <p className="small-meta">
                    {seriesStatus === 'error' ? 'Couldn’t load the series. Refresh to retry.' : 'No series yet.'}
                </p>
            ) : (
                <div className="series-index-list" role="list">
                    {series.map((entry) => (
                        <SeriesIndexCard key={entry.key} entry={entry} postById={postById} />
                    ))}
                </div>
            )}
        </>
    );
}

export default SeriesIndexPage;
